import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { AnimatePresence, motion } from 'framer-motion'

import { Dialog } from '@headlessui/react'

import { SecondStep } from './step/SecondStep'
import { FirstStep } from './step/FirstStep'
import { SelectionModal } from 'components/layout/SelectionModal'
import classNames from 'classnames'
import { isMobile } from 'react-device-detect'
import { ThirdStep } from './step/ThirdStep'
import { ForthStep } from './step/ForthStep'
import { SafetyTip } from './step/SafetyTip'
import { VoiceRecorder } from './step/Recorder'
import { RecorderTest } from './step/RecorderTest'
import { useVoiceCloning } from 'hooks/query/useVoiceCloning'
import { Modal } from 'components/layout/Modal'
import useGetVoiceCloningTicket from 'hooks/query/useGetVoiceCloningTicket'

interface VoiceCloningModalProps {
  isModalOpen: boolean
  setModalOpen: Dispatch<SetStateAction<boolean>>
  setVoiceCloningWaitModalOpen: Dispatch<SetStateAction<boolean>>
  userVoiceRefetch: any
  ticketRefetch: any
}
export const VoiceCloningModal = ({
  isModalOpen,
  setModalOpen,
  setVoiceCloningWaitModalOpen,
  userVoiceRefetch,
  ticketRefetch,
}: PropsWithChildren<VoiceCloningModalProps>) => {
  const PRODUCT_NAME_PATTERN = /^[^<>\uD800-\uDFFF]*$/
  const { mutateAsync: mutate, isPending } = useVoiceCloning()
  const [mode, setMode] = useState<'record' | 'file' | null>(null)

  const [voiceName, setVoiceName] = useState<string>('')
  const [file, setFile] = useState<File | null>(null)
  const [fileDuration, setFileDuration] = useState<number | null>(null)

  const [isVoiceTerms, setVoiceTerms] = useState<boolean>(false)
  const [step, setStep] = useState(0)
  const [direction, setDirection] = useState(1) // 1: 다음, -1: 이전
  const [prevDirection, setPrevDirection] = useState(1) // 👈 이
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false)
  const [errorTitle, setErrorTitle] = useState(<></>)
  const [errorSubTitle, setErrorSubTitle] = useState(<></>)
  const [btnLable, setBtnLabel] = useState<string>('다음')
  const createVoice = async () => {
    // file, name을 가지고 formData로 만들기
    if (!file || !voiceName) {
      if (!file) {
        return alert('file에 문제가 생겼습니다!')
      } else if (!voiceName) {
        return alert('voiceName에 문제가 생겼습니다!')
      }
      return
    }
    const formData = new FormData()
    formData.append('name', voiceName)
    formData.append('file', file)

    try {
      const res = await mutate(formData)

      ticketRefetch()
      setVoiceCloningWaitModalOpen(true)
      setModalOpen(false)
    } catch (e) {
      console.error('voice cloning fail', e)
    }
  }

  const getSteps = () => {
    if (mode === 'file') {
      return [
        {
          element: <FirstStep setMode={setMode} mode={mode} />,
          isNextDisabled: mode === null,
          onNext: null,
          isPrevDeleted: true,
        },
        {
          element: (
            <SecondStep
              isVoiceTerms={isVoiceTerms}
              setVoiceTerms={setVoiceTerms}
            />
          ),
          isNextDisabled: !isVoiceTerms,
          isPrevDeleted: false,
        },
        {
          element: (
            <ThirdStep
              file={file}
              setFile={setFile}
              setErrorModalOpen={setIsErrorModalOpen}
              setErrorSubTitle={setErrorSubTitle}
              setErrorTitle={setErrorTitle}
            />
          ),
          isNextDisabled: !file,
          isPrevDeleted: false,
        },
        {
          element: (
            <ForthStep
              voiceName={voiceName}
              setVoiceName={setVoiceName}
              setBtnLabel={setBtnLabel}
            />
          ),
          isNextDisabled: !voiceName || !PRODUCT_NAME_PATTERN.test(voiceName),
          isPrevDeleted: true,
          onNext: () => {
            createVoice()
          },
        },
      ]
    } else if (mode === 'record') {
      return [
        {
          element: <FirstStep setMode={setMode} mode={mode} />,
          isNextDisabled: mode === null,
          isPrevDeleted: true,
          onNext: null,
        },
        {
          element: <SafetyTip />,
          isNextDisabled: false,
          isPrevDeleted: false,
        },
        {
          element: (
            <SecondStep
              isVoiceTerms={isVoiceTerms}
              setVoiceTerms={setVoiceTerms}
            />
          ),
          isNextDisabled: !isVoiceTerms,
          isPrevDeleted: false,
        },
        {
          element: <RecorderTest />,
          isNextDisabled: false,
          isPrevDeleted: false,
        },
        {
          element: (
            <VoiceRecorder
              setBtnLabel={setBtnLabel}
              setFile={setFile}
              setFileDuration={setFileDuration}
            />
          ),
          isNextDisabled: !file || (fileDuration ?? 0) < 20,
          isPrevDeleted: true,
        },
        {
          element: (
            <ForthStep
              voiceName={voiceName}
              setVoiceName={setVoiceName}
              setBtnLabel={setBtnLabel}
            />
          ),
          isNextDisabled: !voiceName || !PRODUCT_NAME_PATTERN.test(voiceName),
          isPrevDeleted: true,
          onNext: () => {
            createVoice()
          },
        },
      ]
    } else {
      return [
        {
          element: <FirstStep setMode={setMode} mode={mode} />,
          isNextDisabled: mode === null,
          onNext: null,
          isPrevDeleted: true,
        },
      ]
    }
  }

  const steps = getSteps()
  const currentStep = steps[step]
  const nextStep = () => {
    if (step < steps.length - 1) {
      setStep(step + 1)
      setDirection(1)
      setPrevDirection(1)
    }
  }

  const prevStep = () => {
    if (step > 0) {
      setStep(step - 1)
      setDirection(-1)
      setPrevDirection(-1)
    }
  }

  useEffect(() => {
    if (isModalOpen) {
      // 모달이 열릴 때 초기화
      setStep(0)
      setVoiceName('')
      setFile(null)
      setDirection(1)
      setPrevDirection(1)
      setVoiceTerms(false)
      setMode('record')
    }
  }, [isModalOpen])

  return (
    <SelectionModal isModal={isModalOpen}>
      <div
        className={classNames('relative', {
          'bg-white w-[816px] h-auto  rounded-t-[20px] px-8 py-8  flex flex-col  gap-8    ':
            !isMobile,
          'bg-white  w-full h-full   overflow-auto    flex flex-col   gap-8 pt-10 px-4':
            isMobile,
        })}
      >
        {/* 상단 고정 바 */}

        <p className="flex justify-between items-center">
          <span className="font-bold text-[24px]">내 목소리 만들기</span>
          <button
            onClick={() => {
              setModalOpen(false)
            }}
            className=""
          >
            <img
              src="assets/img/Icon_x.png"
              alt="icon_x"
              height={40}
              width={40}
            />
          </button>
        </p>

        {/* 실제 콘텐츠 영역 */}
        <div className="w-full md:min-h-[500px] relative">
          {' '}
          {/* padding-top으로 상단바 높이만큼 여백 줌 */}
          <AnimatePresence mode="wait">
            (
            <motion.div
              className="h-full "
              key={step}
              initial={{ x: direction * 816, opacity: 0.5 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 0, opacity: 0 }} // ✅ 이전 방향 사용
              transition={{ duration: 0.75, ease: 'easeInOut' }}
            >
              {currentStep.element}
            </motion.div>
            )
          </AnimatePresence>
        </div>
      </div>

      <div
        className={classNames(
          `sticky bottom-0 p-8 shadow-main w-full md:w-[816px] bg-white rounded-b-[20px] 
               before:content-[''] before:absolute before:top-[-10px] before:left-0 
               before:w-full before:h-[10px] before:bg-gradient-to-t before:from-black/10 before:to-transparent flex gap-2`,
          {
            'justify-end': step === 0,
          }
        )}
      >
        {!currentStep?.isPrevDeleted && (
          <button
            className="bg-[#EEF1F7] rounded-full  text-[#5E6068] font-bold h-[60px] w-full "
            type="button"
            onClick={prevStep}
            disabled={step === 0}
          >
            이전
          </button>
        )}
        <button
          className="bg-main rounded-full  text-white font-bold h-[60px] w-full disabled:bg-[#EEF1F7] disabled:text-[white] "
          type="button"
          onClick={() => {
            if (currentStep?.onNext) {
              currentStep.onNext()
            } else {
              nextStep()
            }
          }}
          disabled={currentStep.isNextDisabled}
        >
          {btnLable}
        </button>
      </div>

      <Modal isModal={isErrorModalOpen}>
        <div className="bg-white w-[335px] h-auto p-[20px] border-t rounded-t-[20px]">
          <div className="flex flex-col items-center justify-center h-full gap-[24px]">
            <p className="font-bold text-[18px]">{errorTitle}</p>
            <div className="flex flex-col leading-[22px]">{errorSubTitle}</div>
          </div>
        </div>
        <div className="w-[335px] text-[14px]  h-[60px]  flex justify-center items-center">
          <button
            className="text-white w-[100%] h-full bg-main rounded-b-[20px] border-b border-main font-bold"
            onClick={() => {
              setIsErrorModalOpen(false)
            }}
            type="button"
          >
            <span>확인</span>
          </button>
        </div>
      </Modal>
    </SelectionModal>
  )
}
